import React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DISCIPLINES, MATERIAL_BY, newScope } from "@/lib/disciplines";
import { Plus, Trash2, Star, AlertTriangle } from "lucide-react";

// Per-discipline scope lines: lead (names the project), coverage (Full/Partial + % ours),
// and material responsibility (We furnish / Client furnishes / Split %). Fully configurable.
export function ScopeEditor({ value = [], onChange, disabled = false }) {
  const scopes = Array.isArray(value) ? value : [];
  const used = new Set(scopes.map((s) => s.discipline));
  const firstUnused = DISCIPLINES.find((d) => !used.has(d.key))?.key || "other";

  const update = (i, patch) => onChange(scopes.map((s, idx) => (idx === i ? { ...s, ...patch } : s)));
  const setLead = (i) => onChange(scopes.map((s, idx) => ({ ...s, lead: idx === i })));
  const remove = (i) => {
    const next = scopes.filter((_, idx) => idx !== i);
    if (next.length && !next.some((s) => s.lead)) next[0] = { ...next[0], lead: true };
    onChange(next);
  };
  const add = () => {
    if (used.size >= DISCIPLINES.length) return;
    onChange([...scopes, newScope(firstUnused, scopes.length === 0)]);
  };

  return (
    <div data-testid="scope-editor" className="space-y-2">
      {scopes.length === 0 && (
        <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 flex items-center gap-2" data-testid="scope-editor-empty">
          <AlertTriangle className="w-4 h-4 shrink-0" /> Add at least one scope to describe the work.
        </div>
      )}
      {scopes.map((s, i) => {
        const options = DISCIPLINES.filter((d) => d.key === s.discipline || !used.has(d.key));
        return (
          <div key={i} className={`rounded-lg border p-3 ${s.lead ? "border-[#003E87]/40 bg-[#003E87]/5" : "border-slate-200 bg-white"}`} data-testid={`scope-row-${i}`}>
            <div className="flex items-center gap-2">
              <button type="button" disabled={disabled} onClick={() => setLead(i)} data-testid={`scope-lead-${i}`}
                title={s.lead ? "Lead scope — names the project" : "Make this the lead scope"}
                className={`shrink-0 ${s.lead ? "text-[#F4C400]" : "text-slate-300 hover:text-slate-400"} disabled:opacity-60`}>
                <Star className="w-4 h-4" fill={s.lead ? "#F4C400" : "none"} />
              </button>
              <Select value={s.discipline} onValueChange={(v) => update(i, { discipline: v })} disabled={disabled}>
                <SelectTrigger className="h-8 flex-1 text-sm" data-testid={`scope-disc-${i}`}><SelectValue /></SelectTrigger>
                <SelectContent>{options.map((d) => <SelectItem key={d.key} value={d.key}>{d.label}</SelectItem>)}</SelectContent>
              </Select>
              <button type="button" disabled={disabled || scopes.length <= 1} onClick={() => remove(i)} data-testid={`scope-remove-${i}`}
                className="shrink-0 text-slate-300 hover:text-rose-600 disabled:opacity-30"><Trash2 className="w-4 h-4" /></button>
            </div>

            <div className="mt-2.5 flex flex-wrap items-center gap-x-5 gap-y-2">
              <div className="flex items-center gap-1.5">
                <span className="text-[11px] text-slate-500 w-16">Coverage</span>
                {[["full", "Full"], ["partial", "Partial"]].map(([k, lab]) => (
                  <button key={k} type="button" disabled={disabled} data-testid={`scope-comp-${i}-${k}`}
                    onClick={() => update(i, { completeness: k, work_pct: k === "full" ? 100 : (Number(s.work_pct) >= 100 ? 75 : s.work_pct) })}
                    className={`text-[11px] px-2 py-0.5 rounded border transition-colors ${s.completeness === k ? "bg-[#003E87] text-white border-[#003E87]" : "text-slate-600 border-slate-200 hover:bg-slate-50"}`}>{lab}</button>
                ))}
                {s.completeness === "partial" && (
                  <span className="inline-flex items-center gap-1">
                    <Input type="number" min={1} max={99} value={s.work_pct} disabled={disabled}
                      onChange={(e) => update(i, { work_pct: e.target.value })} className="h-7 w-14 text-xs font-mono" data-testid={`scope-work-${i}`} />
                    <span className="text-[11px] text-slate-400">% ours</span>
                  </span>
                )}
              </div>

              <div className="flex items-center gap-1.5">
                <span className="text-[11px] text-slate-500 w-16">Material</span>
                {MATERIAL_BY.map((m) => (
                  <button key={m.key} type="button" disabled={disabled} data-testid={`scope-mat-${i}-${m.key}`}
                    onClick={() => update(i, { material_by: m.key, material_pct: m.key === "split" ? (Number(s.material_pct) || 50) : (m.key === "us" ? 100 : 0) })}
                    className={`text-[11px] px-2 py-0.5 rounded border transition-colors ${s.material_by === m.key ? "bg-[#003E87] text-white border-[#003E87]" : "text-slate-600 border-slate-200 hover:bg-slate-50"}`}>{m.label}</button>
                ))}
                {s.material_by === "split" && (
                  <span className="inline-flex items-center gap-1">
                    <Input type="number" min={0} max={100} value={s.material_pct} disabled={disabled}
                      onChange={(e) => update(i, { material_pct: e.target.value })} className="h-7 w-14 text-xs font-mono" data-testid={`scope-matpct-${i}`} />
                    <span className="text-[11px] text-slate-400">% ours</span>
                  </span>
                )}
              </div>
            </div>

            {s.completeness === "partial" && (
              <Input value={s.completeness_note || ""} disabled={disabled} onChange={(e) => update(i, { completeness_note: e.target.value })}
                placeholder="What portion? e.g. DC only, racking only" className="h-7 mt-2 text-xs" data-testid={`scope-note-${i}`} />
            )}
          </div>
        );
      })}
      {!disabled && used.size < DISCIPLINES.length && (
        <Button type="button" variant="outline" size="sm" onClick={add} data-testid="scope-add" className="w-full border-dashed">
          <Plus className="w-3.5 h-3.5 mr-1.5" /> Add scope
        </Button>
      )}
    </div>
  );
}
